import { useTranslation } from 'react-i18next';

import { env } from '@/env';
import { useWallets } from '@/hooks/useWallets';
import { livelloGiocatoreOf } from '@/lib/wallet/livello';
import { cn } from '@/lib/cn';

/**
 * Il livello del giocatore, dentro la stella QLASH.
 *
 * La forma è l'asset fornito, non una generata: quella calcolata era un
 * poligono regolare e si vedeva — punte tutte uguali, nessun raccordo.
 *
 * I due gradienti puntano a `var(--color-brand)` invece che al ciano fisso
 * dell'SVG originale: così la stella segue l'accento del tema invece di
 * restare l'unico elemento ciano dentro una palette rossa. Il secondo stop è
 * un `color-mix` verso il nero, che riproduce lo scarto dell'asset (#00E7DF →
 * #00ADA7, circa il 25% più scuro) su qualunque accento.
 */
export function LevelBadge({
  className,
  level: levelProp,
}: {
  className?: string;
  /**
   * Il livello da stampare, quando lo conosce già chi chiama.
   *
   * Esiste per la barra dell'esperienza del Profilo: lì il livello serve DUE
   * volte (questo e il prossimo), e farlo ricavare una seconda volta da qui
   * avrebbe permesso ai due numeri di non essere consecutivi.
   */
  level?: number | null;
}) {
  const { t } = useTranslation('hub');
  const { data, isLoading, isError } = useWallets({
    skip: !env.featureQbankEnabled || levelProp != null,
  });

  // Stesso criterio del pannello Ranked: la moneta col LIVELLO è quella
  // dell'esperienza. Si nasconde quando non c'è un livello da mostrare, invece
  // di stampare uno zero: una stella vuota accanto alle valute sembrerebbe un
  // dato mancante, non un dato assente.
  // ⛔ Mai il livello di un Elo: `livelloGiocatoreOf` esclude le monete `ELO-…` e, con piu' di una moneta Exp, non
  // indovina (`lib/wallet/livello.ts`).
  const level = levelProp ?? livelloGiocatoreOf(data);
  if (level == null) return null;
  if (levelProp == null && (!env.featureQbankEnabled || isLoading || isError)) return null;

  return (
    <div className={cn('relative shrink-0', className)} aria-label={t('level.aria', { level })}>
      <svg viewBox="0 0 36 38" className="h-10 w-10" fill="none" aria-hidden>
        <defs>
          <linearGradient id="qlash-star-fill" x1="17.8374" y1="0.1" x2="17.8374" y2="37.1">
            <stop stopColor="var(--color-brand)" />
            <stop offset="1" stopColor="color-mix(in oklab, var(--color-brand) 75%, black)" />
          </linearGradient>
          <linearGradient id="qlash-star-edge" x1="17.8374" y1="0.1" x2="17.8374" y2="37.1">
            <stop
              stopColor="color-mix(in oklab, var(--color-brand) 45%, white)"
              stopOpacity="0.5"
            />
            <stop offset="1" stopColor="color-mix(in oklab, var(--color-brand) 45%, white)" />
          </linearGradient>
        </defs>
        <path
          d="M16.3892 1.62216C17.1773 0.794612 18.4975 0.79461 19.2856 1.62215L21.5737 4.02451C22.039 4.51305 22.7193 4.73408 23.3828 4.61233L26.646 4.01368C27.7701 3.80745 28.8381 4.58342 28.9893 5.71619L29.4283 9.00462C29.5176 9.67336 29.938 10.252 30.5464 10.5436L33.5382 11.9773C34.5688 12.4711 34.9768 13.7267 34.4333 14.732L32.8556 17.6504C32.5347 18.2439 32.5347 18.9592 32.8556 19.5527L34.4333 22.4711C34.9768 23.4764 34.5688 24.732 33.5382 25.2259L30.5464 26.6596C29.938 26.9511 29.5176 27.5298 29.4283 28.1985L28.9893 31.4869C28.8381 32.6197 27.7701 33.3957 26.646 33.1894L23.3828 32.5908C22.7193 32.469 22.039 32.6901 21.5737 33.1786L19.2856 35.581C18.4975 36.4085 17.1773 36.4085 16.3892 35.581L14.1011 33.1786C13.6358 32.6901 12.9556 32.469 12.292 32.5908L9.02881 33.1894C7.90475 33.3957 6.83673 32.6197 6.6855 31.4869L6.24649 28.1985C6.15722 27.5298 5.7368 26.9511 5.12838 26.6596L2.13656 25.2259C1.10596 24.732 0.69801 23.4764 1.24149 22.4711L2.81922 19.5527C3.14007 18.9592 3.14007 18.2439 2.81922 17.6504L1.24149 14.732C0.698012 13.7267 1.10596 12.4711 2.13656 11.9773L5.12838 10.5436C5.7368 10.252 6.15722 9.67336 6.24649 9.00462L6.6855 5.71619C6.83673 4.58342 7.90475 3.80745 9.02881 4.01368L12.292 4.61234C12.9556 4.73408 13.6358 4.51305 14.1011 4.02451L16.3892 1.62216Z"
          fill="url(#qlash-star-fill)"
        />
        <path
          d="M16.0269 1.27734C17.0121 0.242914 18.6627 0.242913 19.6479 1.27734L21.936 3.67969C22.285 4.04598 22.7949 4.21139 23.2925 4.12012L26.5562 3.52148C27.9611 3.26399 29.2959 4.23457 29.4849 5.65039L29.9243 8.93848C29.9913 9.43993 30.306 9.87408 30.7622 10.0928L33.7544 11.5264C35.0425 12.1437 35.5527 13.7131 34.8735 14.9697L33.2954 17.8887C33.055 18.3336 33.055 18.8695 33.2954 19.3145L34.8735 22.2334C35.5527 23.49 35.0425 25.0594 33.7544 25.6768L30.7622 27.1104C30.306 27.329 29.9913 27.7632 29.9243 28.2646L29.4849 31.5527C29.2959 32.9686 27.9611 33.9391 26.5562 33.6816L23.2925 33.083C22.7949 32.9917 22.285 33.1571 21.936 33.5234L19.6479 35.9258C18.6627 36.9602 17.0121 36.9602 16.0269 35.9258L13.7388 33.5234C13.3898 33.1571 12.8799 32.9917 12.3823 33.083L9.11865 33.6816C7.71374 33.9391 6.37895 32.9686 6.18994 31.5527L5.75049 28.2646C5.68352 27.7632 5.36878 27.329 4.9126 27.1104L1.92041 25.6768C0.63227 25.0594 0.12209 23.49 0.80127 22.2334L2.37939 19.3145C2.61977 18.8695 2.61977 18.3336 2.37939 17.8887L0.80127 14.9697C0.122093 13.7131 0.632272 12.1437 1.92041 11.5264L4.9126 10.0928C5.36878 9.87408 5.68352 9.43993 5.75049 8.93848L6.18994 5.65039C6.37895 4.23457 7.71374 3.26399 9.11865 3.52148L12.3823 4.12012C12.8799 4.21139 13.3898 4.04598 13.7388 3.67969L16.0269 1.27734Z"
          stroke="url(#qlash-star-edge)"
          strokeOpacity="0.38"
        />
      </svg>
      {/* Il centro OTTICO della stella non coincide col centro della sua
          scatola: il viewBox e' 36x38 e la punta superiore parte piu' in alto
          di quanto quella inferiore scenda. Centrare sulla scatola lasciava il
          numero visibilmente basso. `leading-none` toglie di mezzo la spaziatura
          di riga del font, che spingeva ancora piu' giu'. */}
      <span className="absolute inset-0 flex -translate-y-[3%] items-center justify-center text-sm font-black leading-none tabular-nums text-brand-fg">
        {level}
      </span>
    </div>
  );
}
